import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import {
  Typography,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  CircularProgress,
  IconButton,
  Chip
} from '@mui/material';
import { CloudUpload, AccountTree, Delete, HelpOutline } from '@mui/icons-material';
import axios from 'axios';
import { ThemeModeContext } from '../index';
import { API_BASE_URL } from '../config/api';
import logger from '../utils/logger';
import WorkspaceLayout from '../components/WorkspaceLayout';
import OptionsPanel, { useCollapsiblePanel } from '../components/OptionsPanel';
import SegmentedControl from '../components/SegmentedControl';
import ExportScriptsDialog from '../components/ExportScriptsDialog';
import DiagramPanel from '../components/diagram/DiagramPanel';
import DiagramOptions from '../components/diagram/DiagramOptions';

const ALL_PLATFORMS = ['Windows', 'macOS', 'iOS', 'Android'];
const ICON_LIMIT = 500;
const MAX_FILE_BYTES = 100 * 1024 * 1024;

// Work out whether an uploaded payload describes apps or configuration profiles.
const detectDataType = (data) => {
  if (data.deviceConfigurations || data.configurationPolicies) return 'Profiles';

  const dataArray = data.value || data.mobileApps || (Array.isArray(data) ? data : null);
  const firstItem = dataArray?.[0];
  if (!firstItem) return 'Applications';

  const odataType = firstItem['@odata.type'] || '';
  if (firstItem.templateReference || odataType.includes('Configuration') || odataType.includes('Policy')) {
    return 'Profiles';
  }
  if (odataType.includes('App') || firstItem.platform || firstItem.largeIcon) {
    return 'Applications';
  }
  return 'Applications';
};

const itemsFromData = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.value)) return data.value;
  if (Array.isArray(data.mobileApps)) return data.mobileApps;
  if (Array.isArray(data.applications)) return data.applications;
  if (Array.isArray(data.profiles)) return data.profiles;
  if (data.deviceConfigurations || data.configurationPolicies) {
    return [...(data.deviceConfigurations || []), ...(data.configurationPolicies || [])];
  }
  return [];
};

const countEntries = (data) => {
  const items = itemsFromData(data);
  if (items.length > 0) return items.length;
  return typeof data === 'object' && data !== null ? 1 : 0;
};

// Scope tags arrive as resolved display names from the export scripts.
const extractScopeTagNames = (data) => {
  const names = new Set();

  itemsFromData(data).forEach((item) => {
    let tagNames = item.roleScopeTagNames;
    if (typeof tagNames === 'string') tagNames = [tagNames];
    if (!Array.isArray(tagNames)) return;
    tagNames.forEach((name) => {
      if (name && typeof name === 'string') names.add(name);
    });
  });

  return Array.from(names).sort((a, b) => {
    if (a === 'Default') return -1;
    if (b === 'Default') return 1;
    return a.localeCompare(b);
  });
};

const Offline = () => {
  const { actualMode } = useContext(ThemeModeContext);
  const fileInputRef = useRef(null);

  const [panelCollapsed, togglePanel] = useCollapsiblePanel('offlineOptionsCollapsed');

  const [formData, setFormData] = useState({
    type: 'Applications',
    groupBy: 'Name',
    operatingSystem: ALL_PLATFORMS,
    direction: 'TB',
    displayIcons: true,
    policyType: [],
    applicationType: [],
    appendVersion: false,
    excludeSupersededApps: false,
    layout: 'default',
    theme: actualMode === 'dark' ? 'dark' : 'default'
  });

  const [uploadedData, setUploadedData] = useState(null);
  const [availableScopeTags, setAvailableScopeTags] = useState([]);
  const [selectedScopeTags, setSelectedScopeTags] = useState([]);
  const [mermaidCode, setMermaidCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [scriptsOpen, setScriptsOpen] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, theme: actualMode === 'dark' ? 'dark' : 'default' }));
  }, [actualMode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updates = { [field]: value };

      if (field === 'type') {
        updates.groupBy = 'Name';
        updates.operatingSystem = ALL_PLATFORMS;
        updates.direction = 'TB';
        updates.displayIcons = true;
        updates.policyType = [];
        updates.applicationType = [];
        updates.appendVersion = false;
        updates.excludeSupersededApps = false;
        updates.layout = 'default';
      }

      return { ...prev, ...updates };
    });

    if (field === 'type') setSelectedScopeTags([]);
  };

  const handleMultiSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleFileSelection = async (file) => {
    if (!file) return;

    if (!file.type.includes('json') && !file.name.endsWith('.json')) {
      setError('Please select a JSON file');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('File size must be less than 100MB');
      return;
    }

    setLoading(true);
    setLoadingStage('uploading');
    setError(null);

    try {
      const body = new FormData();
      body.append('jsonFile', file);

      const response = await axios.post(`${API_BASE_URL}/diagrams/upload`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      const data = response.data.data;
      const detectedType = detectDataType(data);
      const entryCount = countEntries(data);

      // Icons are expensive to render; turn them off for big uploads.
      const shouldDisableIcons = detectedType === 'Applications' && entryCount > ICON_LIMIT;

      setFormData((prev) => ({
        ...prev,
        type: detectedType,
        displayIcons: shouldDisableIcons ? false : prev.displayIcons
      }));

      setUploadedData(data);
      setFileInfo({ ...response.data.fileInfo, detectedType, entryCount });

      const scopeTags = extractScopeTagNames(data);
      setAvailableScopeTags(scopeTags);
      setSelectedScopeTags([]);

      let message = 'File uploaded successfully!';
      if (shouldDisableIcons) {
        message += ` (Display Icons automatically disabled for ${entryCount} entries - improves performance)`;
      }
      if (scopeTags.length > 0) {
        message += ` Found ${scopeTags.length} scope tag${scopeTags.length > 1 ? 's' : ''} in data.`;
      }
      setSuccess(message);
    } catch (err) {
      logger.error('File upload failed:', err.response?.data || err);
      const errorMessage = err.response?.data?.error || 'Failed to upload file';
      const errorDetails = err.response?.data?.details;
      setError(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
    } finally {
      setLoading(false);
      setLoadingStage(null);
    }
  };

  const generateDiagram = async () => {
    if (!uploadedData) {
      setError('Please upload a JSON file first');
      return;
    }

    setLoading(true);
    setLoadingStage('generating');
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/diagrams/offline`,
        {
          ...formData,
          data: uploadedData,
          // Omitting scopeTagNames means "all".
          scopeTagNames: selectedScopeTags.length > 0 ? selectedScopeTags : undefined
        },
        { withCredentials: true }
      );

      const code = response.data.mermaidCode || '';

      if (/MODULE_NOT_FOUND/.test(code)) {
        setError('Server: IntuneMermaid module not found. Install module to enable diagram generation.');
      } else if (/OFFLINE_DATAFILE_MISSING/.test(code)) {
        setError('Server reported missing offline data file. Retry the operation.');
      } else if (!code.trim() || code.trim() === 'flowchart TB') {
        // An empty diagram almost always means the data is missing fields the
        // generator needs, so say which ones rather than showing a blank canvas.
        const firstItem = itemsFromData(uploadedData)[0];
        const reasons = [];

        if (!firstItem) {
          reasons.push('No data items found in the uploaded file');
        } else {
          if (!firstItem.assignments || firstItem.assignments.length === 0) {
            reasons.push('Data items have no assignments (required for diagram generation)');
          }
          if (!firstItem.displayName && !firstItem.name) {
            reasons.push('Data items missing displayName/name property');
          }
          if (!firstItem['@odata.type']) {
            reasons.push('Data items missing @odata.type property');
          }
        }
        reasons.push('All items were filtered out by your current Operating System or Type selections');

        setError(
          `No diagram content generated. This usually means:\n\n${reasons
            .map((r) => `• ${r}`)
            .join('\n')}\n\nEnsure your data matches the required format with proper assignments.`
        );
      } else {
        setMermaidCode(code);
        setSuccess('Diagram generated successfully!');
      }
    } catch (err) {
      logger.error('Failed to generate diagram:', err.response?.data || err);
      const errorMsg = err.response?.data?.error || 'Failed to generate diagram';
      const errorDetails = err.response?.data?.details || '';
      const stderr = err.response?.data?.stderr || '';
      setError(`${errorMsg}${errorDetails ? ': ' + errorDetails : ''}${stderr ? '\n' + stderr : ''}`);
    } finally {
      setLoading(false);
      setLoadingStage(null);
    }
  };

  const clearUploadedData = () => {
    setUploadedData(null);
    setFileInfo(null);
    setMermaidCode('');
    setError(null);
    setSuccess(null);
    setAvailableScopeTags([]);
    setSelectedScopeTags([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const panelSummary = useMemo(() => {
    const items = [{ label: 'Source', value: fileInfo ? fileInfo.detectedType : 'No file' }];
    if (fileInfo?.entryCount) {
      items.push({ label: 'Objects', value: `${fileInfo.entryCount} objects` });
    }
    items.push({ label: 'Group by', value: formData.groupBy });
    return items;
  }, [fileInfo, formData.groupBy]);

  const busyTitle =
    loadingStage === 'uploading'
      ? 'Uploading Intune data...'
      : loadingStage === 'generating'
        ? 'Generating diagram...'
        : undefined;

  const busySubtitle =
    loadingStage === 'uploading'
      ? 'Processing your JSON file'
      : loadingStage === 'generating'
        ? 'Creating Mermaid diagram from your data'
        : undefined;

  const optionsContent = (
    <>
      <Paper
        variant="outlined"
        sx={{
          py: 1,
          px: 2,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          backgroundColor: dragOver ? 'action.hover' : 'transparent',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          mb: 1.25,
          '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length > 0) handleFileSelection(e.dataTransfer.files[0]);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={(e) => {
            if (e.target.files.length > 0) handleFileSelection(e.target.files[0]);
          }}
          style={{ display: 'none' }}
        />
        <CloudUpload sx={{ fontSize: 24, color: 'text.secondary' }} />
        <Typography variant="body2">Drop JSON file here, or click to browse</Typography>
        <Typography variant="caption" color="text.secondary">
          .json up to 100MB
        </Typography>
      </Paper>

      <Button
        size="small"
        fullWidth
        startIcon={<HelpOutline />}
        onClick={() => setScriptsOpen(true)}
        sx={{ mb: 1 }}
      >
        How to export your Intune data
      </Button>

      {fileInfo && (
        <Alert
          severity="success"
          sx={{ mb: 1.25, py: 0.25 }}
          action={
            <IconButton size="small" onClick={clearUploadedData} aria-label="Remove uploaded file">
              <Delete />
            </IconButton>
          }
        >
          <Typography variant="caption" display="block">
            <strong>Type:</strong> {fileInfo.detectedType}
          </Typography>
          <Typography variant="caption" display="block">
            <strong>Objects Detected:</strong> {fileInfo.entryCount || 1}
          </Typography>
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1, mt: 0.5, fontWeight: 600, color: 'primary.main' }}>
        Intune Options
      </Typography>

      <SegmentedControl
        label="Group by"
        value={formData.groupBy}
        onChange={(value) => handleInputChange('groupBy', value)}
        options={[
          { value: 'Name', label: 'Name' },
          { value: 'Assignments', label: 'Assignments' }
        ]}
        sx={{ mb: formData.groupBy === 'Assignments' ? 0.5 : 1.25 }}
      />
      {formData.groupBy === 'Assignments' && (
        <FormHelperText sx={{ mb: 1.25, mt: 0 }}>
          Grouping by assignments needs group data in your JSON.
        </FormHelperText>
      )}

      <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
        <InputLabel>Operating Systems</InputLabel>
        <Select
          multiple
          value={formData.operatingSystem}
          label="Operating Systems"
          onChange={(e) => handleMultiSelectChange('operatingSystem', e.target.value)}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {ALL_PLATFORMS.map((os) => (
            <MenuItem key={os} value={os}>
              {os}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {availableScopeTags.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel>Scope Tags</InputLabel>
          <Select
            multiple
            value={selectedScopeTags}
            label="Scope Tags"
            onChange={(e) => setSelectedScopeTags(e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    All scope tags
                  </Typography>
                ) : (
                  selected.map((value) => <Chip key={value} label={value} size="small" />)
                )}
              </Box>
            )}
          >
            {availableScopeTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Filter by scope tags (leave empty for all)</FormHelperText>
        </FormControl>
      )}

      {formData.type === 'Applications' && (
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel>Advanced Options</InputLabel>
          <Select
            multiple
            value={[
              formData.displayIcons && 'Display Icons',
              formData.appendVersion && 'Append Version',
              formData.excludeSupersededApps && 'Exclude Superseded Apps'
            ].filter(Boolean)}
            label="Advanced Options"
            onChange={(e) => {
              const selected = e.target.value;
              setFormData((prev) => ({
                ...prev,
                displayIcons: selected.includes('Display Icons'),
                appendVersion: selected.includes('Append Version'),
                excludeSupersededApps: selected.includes('Exclude Superseded Apps')
              }));
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            <MenuItem value="Display Icons">Display Icons</MenuItem>
            <MenuItem value="Append Version">Append Version</MenuItem>
            <MenuItem value="Exclude Superseded Apps">Exclude Superseded Apps</MenuItem>
          </Select>
          {fileInfo && fileInfo.entryCount > ICON_LIMIT && (
            <FormHelperText>
              Display Icons automatically disabled for datasets with more than {ICON_LIMIT} entries to
              improve performance
            </FormHelperText>
          )}
        </FormControl>
      )}

      <DiagramOptions formData={formData} onChange={handleInputChange} />

      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
    </>
  );

  const generateButton = (
    <Button
      variant="contained"
      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AccountTree />}
      onClick={generateDiagram}
      disabled={loading || !uploadedData}
      fullWidth
    >
      {loading ? 'Generating...' : 'Generate Diagram'}
    </Button>
  );

  return (
    <>
      <WorkspaceLayout
        panel={
          <OptionsPanel
            collapsed={panelCollapsed}
            onToggle={togglePanel}
            title="Offline Options"
            summary={panelSummary}
            footer={generateButton}
          >
            {optionsContent}
          </OptionsPanel>
        }
      >
        <DiagramPanel
          mermaidCode={mermaidCode}
          theme={formData.theme}
          layout={formData.layout}
          busy={loading}
          busyTitle={busyTitle}
          busySubtitle={busySubtitle}
          emptyTitle={uploadedData ? 'No Diagram Generated' : 'No Data Uploaded'}
          emptySubtitle={
            uploadedData
              ? 'Configure your settings and click "Generate Diagram" to create a visualization'
              : 'Upload a JSON export of your Intune data to get started'
          }
          onError={setError}
          onSuccess={setSuccess}
        />
      </WorkspaceLayout>

      {/* With the panel collapsed the inline alerts are hidden - float them. */}
      {panelCollapsed && (error || success) && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1300, maxWidth: 420 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1, whiteSpace: 'pre-line' }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
        </Box>
      )}

      <ExportScriptsDialog
        open={scriptsOpen}
        onClose={() => setScriptsOpen(false)}
        onCopied={setSuccess}
        onCopyFailed={setError}
      />
    </>
  );
};

export default Offline;
